import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const PageHeader = styled.div`
  padding: 8rem 0 4rem;
  background-color: var(--color-primary-dark);
  color: var(--color-light);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to right bottom, var(--color-primary-dark) 49%, var(--color-white) 50%);
  }
`;

const PageHeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  max-width: 700px;
`;

const ProjectsContainer = styled.section`
  padding: 6rem 0;
`;

const ProjectsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: var(--color-white);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ProjectImage = styled.div`
  height: 220px;
  background-size: cover;
  background-position: center;
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ProjectCategory = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: var(--color-accent);
  color: var(--color-white);
  border-radius: 4px;
  font-size: 0.8rem;
  margin-bottom: 1rem;
  align-self: flex-start;
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ProjectDescription = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.7;
`;

const ProjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: auto;
  gap: 0.5rem;
`;

const ProjectTag = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: var(--color-gray);
  border-radius: 4px;
  font-size: 0.8rem;
`;

const FeaturedProject = styled.section`
  padding: 6rem 0;
  background-color: var(--color-gray);
`;

const FeaturedContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FeaturedTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FeaturedContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FeaturedImageContainer = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  height: 400px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const FeaturedImage = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
`;

const FeaturedDetails = styled.div``;

const FeaturedCategory = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: var(--color-accent);
  color: var(--color-white);
  border-radius: 4px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const FeaturedProjectTitle = styled.h3`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FeaturedDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeaturedResults = styled.div`
  margin: 2rem 0;
  padding: 1.5rem;
  background-color: var(--color-white);
  border-radius: 8px;
  border-left: 4px solid var(--color-accent);
`;

const ResultsTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const ResultsList = styled.ul`
  margin-bottom: 0;
  padding-left: 1.5rem;
`;

const ResultItem = styled.li`
  margin-bottom: 0.5rem;
  position: relative;
  
  &:before {
    content: '✓';
    color: var(--color-accent);
    position: absolute;
    left: -1.5rem;
    font-weight: bold;
  }
`;

const FeaturedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  padding: 0.8rem 2rem;
  background: var(--color-accent);
  color: var(--color-white);
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  
  &:hover {
    background: var(--color-primary-dark);
    color: var(--color-white);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const Projects = () => {
  return (
    <>
      <PageHeader>
        <PageHeaderContent>
          <PageTitle>Project Snapshots</PageTitle>
          <PageSubtitle>
            Explore a selection of our most impactful solutions across industries
          </PageSubtitle>
        </PageHeaderContent>
      </PageHeader>
      
      <FeaturedProject>
        <FeaturedContainer>
          <FeaturedTitle>Featured Project</FeaturedTitle>
          <FeaturedContent>
            <FeaturedImageContainer>
              <FeaturedImage style={{ backgroundImage: 'url("/images/featured-project.jpg")' }} />
            </FeaturedImageContainer>
            
            <FeaturedDetails>
              <FeaturedCategory>Enterprise Architecture</FeaturedCategory>
              <FeaturedProjectTitle>Global Healthcare Platform</FeaturedProjectTitle>
              <FeaturedDescription>
                We designed and implemented a scalable, HIPAA-compliant architecture for a healthcare provider serving millions of patients worldwide. The solution integrated multiple legacy systems while ensuring data security and high availability.
              </FeaturedDescription>
              
              <FeaturedResults>
                <ResultsTitle>Key Results</ResultsTitle>
                <ResultsList>
                  <ResultItem>99.99% system availability, exceeding healthcare industry standards</ResultItem>
                  <ResultItem>45% reduction in infrastructure costs through efficient cloud architecture</ResultItem>
                  <ResultItem>72% faster data retrieval for critical patient information</ResultItem>
                  <ResultItem>Successful integration of 12 disparate legacy systems</ResultItem>
                </ResultsList>
              </FeaturedResults>
              
              <FeaturedTags>
                <ProjectTag>Cloud Architecture</ProjectTag>
                <ProjectTag>HIPAA Compliance</ProjectTag>
                <ProjectTag>High Availability</ProjectTag>
                <ProjectTag>Legacy Integration</ProjectTag>
                <ProjectTag>AWS</ProjectTag>
              </FeaturedTags>
              
              <CtaButton to="/contact">Discuss Similar Projects</CtaButton>
            </FeaturedDetails>
          </FeaturedContent>
        </FeaturedContainer>
      </FeaturedProject>
      
      <ProjectsContainer>
        <ProjectsGrid>
          <ProjectCard>
            <ProjectImage style={{ backgroundImage: 'url("/images/project1.jpg")' }} />
            <ProjectContent>
              <ProjectCategory>System Design</ProjectCategory>
              <ProjectTitle>Financial Services API Platform</ProjectTitle>
              <ProjectDescription>
                Designed a high-throughput API platform for a financial services provider, enabling secure real-time transactions and third-party integrations.
              </ProjectDescription>
              <ProjectTags>
                <ProjectTag>API Architecture</ProjectTag>
                <ProjectTag>Microservices</ProjectTag>
                <ProjectTag>Azure</ProjectTag>
                <ProjectTag>Security</ProjectTag>
              </ProjectTags>
            </ProjectContent>
          </ProjectCard>
          
          <ProjectCard>
            <ProjectImage style={{ backgroundImage: 'url("/images/project2.jpg")' }} />
            <ProjectContent>
              <ProjectCategory>Cloud Transformation</ProjectCategory>
              <ProjectTitle>E-commerce Platform Migration</ProjectTitle>
              <ProjectDescription>
                Orchestrated the migration of a high-traffic e-commerce platform from on-premises to cloud infrastructure while maintaining zero downtime.
              </ProjectDescription>
              <ProjectTags>
                <ProjectTag>AWS</ProjectTag>
                <ProjectTag>Kubernetes</ProjectTag>
                <ProjectTag>CI/CD</ProjectTag>
                <ProjectTag>Zero-downtime</ProjectTag>
              </ProjectTags>
            </ProjectContent>
          </ProjectCard>
          
          <ProjectCard>
            <ProjectImage style={{ backgroundImage: 'url("/images/project3.jpg")' }} />
            <ProjectContent>
              <ProjectCategory>Data Architecture</ProjectCategory>
              <ProjectTitle>Analytics Data Pipeline</ProjectTitle>
              <ProjectDescription>
                Designed and implemented a robust data pipeline handling billions of events daily for a digital marketing platform, enabling real-time analytics.
              </ProjectDescription>
              <ProjectTags>
                <ProjectTag>Big Data</ProjectTag>
                <ProjectTag>Kafka</ProjectTag>
                <ProjectTag>Spark</ProjectTag>
                <ProjectTag>GCP</ProjectTag>
              </ProjectTags>
            </ProjectContent>
          </ProjectCard>
          
          <ProjectCard>
            <ProjectImage style={{ backgroundImage: 'url("/images/project4.jpg")' }} />
            <ProjectContent>
              <ProjectCategory>Security Architecture</ProjectCategory>
              <ProjectTitle>Secure Payment Processing</ProjectTitle>
              <ProjectDescription>
                Architected a PCI-compliant payment processing system for a global retailer, ensuring security while maintaining excellent user experience.
              </ProjectDescription>
              <ProjectTags>
                <ProjectTag>PCI Compliance</ProjectTag>
                <ProjectTag>Encryption</ProjectTag>
                <ProjectTag>Tokenization</ProjectTag>
                <ProjectTag>AWS</ProjectTag>
              </ProjectTags>
            </ProjectContent>
          </ProjectCard>
          
          <ProjectCard>
            <ProjectImage style={{ backgroundImage: 'url("/images/project5.jpg")' }} />
            <ProjectContent>
              <ProjectCategory>Microservices</ProjectCategory>
              <ProjectTitle>Media Streaming Platform</ProjectTitle>
              <ProjectDescription>
                Redesigned a monolithic media platform into a scalable microservices architecture, enabling independent scaling and faster feature delivery.
              </ProjectDescription>
              <ProjectTags>
                <ProjectTag>Microservices</ProjectTag>
                <ProjectTag>Docker</ProjectTag>
                <ProjectTag>Kubernetes</ProjectTag>
                <ProjectTag>Event-driven</ProjectTag>
              </ProjectTags>
            </ProjectContent>
          </ProjectCard>
          
          <ProjectCard>
            <ProjectImage style={{ backgroundImage: 'url("/images/project6.jpg")' }} />
            <ProjectContent>
              <ProjectCategory>DevOps Transformation</ProjectCategory>
              <ProjectTitle>CI/CD Pipeline Modernization</ProjectTitle>
              <ProjectDescription>
                Implemented a modern CI/CD pipeline for a financial software provider, reducing deployment time from days to minutes and improving reliability.
              </ProjectDescription>
              <ProjectTags>
                <ProjectTag>GitLab</ProjectTag>
                <ProjectTag>Infrastructure as Code</ProjectTag>
                <ProjectTag>Monitoring</ProjectTag>
                <ProjectTag>Automation</ProjectTag>
              </ProjectTags>
            </ProjectContent>
          </ProjectCard>
        </ProjectsGrid>
      </ProjectsContainer>
    </>
  );
};

export default Projects;